/**
 * AhriCard — AHRI-01 on the job's Equipment & Accessories (Seth 2026-09-25).
 * Type the AHRI number → Look It Up: a heat pump / mini-split fills from
 * ENERGY STAR's free list (models + SEER2 / EER2 / HSPF2); an AC system or a
 * furnace is typed off the certificate. Each model on the certificate is checked
 * against the units on the job; one that isn't on the job but is in the
 * pricebook adds with one click (cost prefilled). The certificate PDF is a job
 * file tagged "AHRI Certificate". Data + the rules: ./ahri.ts.
 */
import { useRef, useState } from "react";
import { BadgeCheck, Check, ExternalLink, FileText, Loader2, Paperclip, Pencil, Plus, Search, Sparkles, Trash2 } from "lucide-react";
import { cn } from "@/lib/cn";
import { Button } from "@/components/ui/Button";
import { InfoTip } from "@/components/InfoTip";
import { CopyCode } from "@/components/Copyable";
import { useJobFiles, useUploadFiles, type FileAiData, type PegasusFile } from "@/features/files/filesCore";
import { FilePreviewModal } from "@/features/files/FilePreviewModal";
import { useAddJobEquipment, useJobEquipment, type Job, type JobEquipment } from "./jobs";
import {
  AHRI_TYPES, AHRI_TYPE_LABEL, ahriLink, cleanAhriRef, emptyAhriDraft, lookupAhri, modelMatches, ratingText,
  useDeleteAhri, useJobAhri, usePricebookByModel, useSaveAhri,
  type AhriDraft, type AhriSystemType, type JobAhri,
} from "./ahri";

type CertAhri = NonNullable<FileAiData["ahri"]>;
/** FILES-AI-01: the AHRI certificate's numbers, as the AI read them → a draft to check and save. */
function draftFromCert(a: CertAhri): AhriDraft {
  const st = (AHRI_TYPES as string[]).includes(a.system_type ?? "") ? (a.system_type as AhriSystemType) : null;
  return {
    ...emptyAhriDraft(a.ahri_ref), system_type: st, brand: a.brand, series: a.series,
    outdoor_model: a.outdoor_model, indoor_model: a.indoor_model, furnace_model: a.furnace_model,
    seer2: a.seer2, eer2: a.eer2, hspf2: a.hspf2, cooling_btuh: a.cooling_btuh, heating_btuh: a.heating_btuh, afue: a.afue,
    source: "typed",
  };
}

const inp = "w-full rounded-lg border border-hairline bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30";
const btu = (n: number | null) => (n == null ? null : `${Math.round(n).toLocaleString("en-US")} Btu/h`);

export function AhriCard({ job, closed }: { job: Job; closed: boolean }) {
  const { data: rows } = useJobAhri(job.id);
  const { data: equip } = useJobEquipment(job.id);
  const { data: files } = useJobFiles(job.id);
  const [adding, setAdding] = useState<false | { draft?: AhriDraft }>(false);
  const [editing, setEditing] = useState<string | null>(null);
  const list = rows ?? [];
  // FILES-AI-01: an attached certificate the AI has read, whose number isn't on the job yet.
  const certs = (files ?? []).filter((f) => f.doc_tag === "ahri_cert");
  const known = new Set(list.map((r) => cleanAhriRef(r.ahri_ref)));
  const fromCerts = certs.map((f) => f.ai_data?.ahri).filter((a): a is CertAhri => !!a && !known.has(cleanAhriRef(a.ahri_ref)))
    .filter((a, i, all) => all.findIndex((b) => b.ahri_ref === a.ahri_ref) === i);
  const reading = certs.some((f) => !f.ai_at && Date.now() - new Date(f.created_at).getTime() < 3 * 60 * 1000);

  return (
    <div className="mt-4 rounded-xl border border-hairline bg-slate-50/60 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <BadgeCheck className="h-5 w-5 text-brand-600" />
          <h3 className="text-[15px] font-bold text-slate-900">AHRI Match</h3>
          <InfoTip label="What the AHRI number is">
            The AHRI certified reference number proves the outdoor unit, the coil and the furnace were rated together —
            utility rebates ask for it. Look It Up fills the models and ratings from ENERGY STAR&apos;s free list when the
            system is on it (heat pumps and mini-splits). ENERGY STAR stopped listing central air conditioners in
            February 2026, so an AC system or a furnace is typed off the AHRI certificate.
          </InfoTip>
        </div>
        {!closed && !adding && (
          <Button size="sm" variant="secondary" onClick={() => { setAdding({}); setEditing(null); }}>
            <Plus className="h-4 w-4" /> Add AHRI Number
          </Button>
        )}
      </div>
      {!closed && !adding && fromCerts.map((a) => (
        <div key={a.ahri_ref} className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-violet-200 bg-violet-50/70 px-3 py-2.5 text-sm">
          <Sparkles className="h-4 w-4 shrink-0 text-violet-600" />
          <span className="min-w-0 flex-1 text-slate-700">
            The certificate reads <b className="font-semibold tabular-nums text-slate-900">AHRI {a.ahri_ref}</b>
            {[a.brand, ratingText({ seer2: a.seer2, eer2: a.eer2, hspf2: a.hspf2, afue: a.afue })].filter(Boolean).map((x) => ` · ${x}`).join("")}
          </span>
          <Button size="sm" onClick={() => { setAdding({ draft: draftFromCert(a) }); setEditing(null); }}>
            <Check className="h-4 w-4" /> Fill It In
          </Button>
        </div>
      ))}
      {!closed && !adding && reading && fromCerts.length === 0 && (
        <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-violet-700"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading the certificate…</p>
      )}
      {list.length === 0 && !adding && fromCerts.length === 0 && !reading && (
        <p className="mt-2 text-sm text-slate-500">No AHRI number yet.</p>
      )}
      <div className={cn("space-y-3", (list.length > 0 || adding) && "mt-3")}>
        {list.map((r) => (editing === r.id
          ? <AhriForm key={r.id} jobId={job.id} existing={r} onDone={() => setEditing(null)} />
          : <AhriRow key={r.id} r={r} job={job} equip={equip ?? []} closed={closed} onEdit={() => { setEditing(r.id); setAdding(false); }} />))}
        {adding && <AhriForm jobId={job.id} initial={adding.draft} onDone={() => setAdding(false)} />}
      </div>
      <AhriCertificates job={job} certs={certs} closed={closed} />
    </div>
  );
}

function AhriRow({ r, job, equip, closed, onEdit }: { r: JobAhri; job: Job; equip: JobEquipment[]; closed: boolean; onEdit: () => void }) {
  const del = useDeleteAhri();
  const [sure, setSure] = useState(false);
  const rating = ratingText(r);
  const caps = [
    r.cooling_btuh != null ? `Cooling ${btu(r.cooling_btuh)}` : null,
    r.heating_btuh != null ? (r.system_type === "furnace" ? `Output ${btu(r.heating_btuh)}` : `Heating ${btu(r.heating_btuh)} at 47°F`) : null,
  ].filter(Boolean).join(" · ");
  return (
    <div className="rounded-lg border border-hairline bg-white p-3.5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-base font-bold text-slate-900">AHRI</span>
            <CopyCode value={r.ahri_ref} label="Copy AHRI number" size="md" />
            {r.system_type && <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600">{AHRI_TYPE_LABEL[r.system_type]}</span>}
            {r.source === "energy_star" && (
              <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">From ENERGY STAR</span>
            )}
            {r.cold_climate && <span className="rounded-full bg-sky-50 px-2.5 py-0.5 text-xs font-semibold text-sky-700 ring-1 ring-inset ring-sky-200">Cold Climate</span>}
          </div>
          {(r.brand || r.series) && <div className="mt-1 text-sm text-slate-600">{[r.brand, r.series].filter(Boolean).join(" · ")}</div>}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <a href={ahriLink(r.ahri_ref, r.system_type)} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-sm font-semibold text-brand-700 transition-colors hover:bg-brand-50">
            View on AHRI <ExternalLink className="h-3.5 w-3.5" />
          </a>
          {!closed && (
            <button type="button" onClick={onEdit} title="Edit" className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700">
              <Pencil className="h-4 w-4" />
            </button>
          )}
          {!closed && (sure ? (
            <span className="inline-flex items-center gap-1 text-sm">
              <button type="button" onClick={() => del.mutate(r.id)} className="rounded-lg bg-rose-600 px-2 py-1 font-semibold text-white hover:bg-rose-700">Remove</button>
              <button type="button" onClick={() => setSure(false)} className="rounded-lg px-2 py-1 font-medium text-slate-500 hover:bg-slate-100">Keep</button>
            </span>
          ) : (
            <button type="button" onClick={() => setSure(true)} title="Remove from this job" className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600">
              <Trash2 className="h-4 w-4" />
            </button>
          ))}
        </div>
      </div>
      {rating && <div className="mt-2 text-[15px] font-semibold tabular-nums text-slate-900">{rating}</div>}
      {caps && <div className="text-sm tabular-nums text-slate-500">{caps}</div>}
      {(r.outdoor_model || r.indoor_model || r.furnace_model) && (
        <div className="mt-2.5 space-y-1.5 border-t border-hairline pt-2.5">
          <ModelLine label="Outdoor Unit" model={r.outdoor_model} job={job} equip={equip} closed={closed} />
          <ModelLine label="Coil / Air Handler" model={r.indoor_model} job={job} equip={equip} closed={closed} />
          <ModelLine label="Furnace" model={r.furnace_model} job={job} equip={equip} closed={closed} />
        </div>
      )}
      {r.note && <p className="mt-2 text-sm text-slate-600">{r.note}</p>}
    </div>
  );
}

/** One model off the certificate: on the job ✓, or in the pricebook → Add to Job. */
function ModelLine({ label, model, job, equip, closed }: { label: string; model: string | null; job: Job; equip: JobEquipment[]; closed: boolean }) {
  const onJob = model ? equip.find((e) => modelMatches(model, e.model) || modelMatches(model, e.part_number)) : undefined;
  const { data: hits, isLoading } = usePricebookByModel(model && !onJob ? model : null);
  const add = useAddJobEquipment();
  if (!model) return null;
  const hit = hits?.[0];
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
      <span className="w-40 shrink-0 text-slate-500">{label}</span>
      <CopyCode value={model} label={`Copy ${label.toLowerCase()} model`} />
      {onJob ? (
        <span className="inline-flex items-center gap-1 font-semibold text-emerald-700">
          <Check className="h-4 w-4" /> On this job{onJob.serial ? ` · S/N ${onJob.serial}` : ""}
        </span>
      ) : isLoading ? (
        <span className="text-slate-400">Checking the pricebook…</span>
      ) : hit ? (
        <>
          <span className="text-amber-700">Not on this job</span>
          <span className="text-slate-500">· in the pricebook: {hit.description || hit.model || hit.part_number}</span>
          {!closed && (
            <button type="button" disabled={add.isPending}
              onClick={() => add.mutate({
                job_id: job.id, category: hit.category ?? null, manufacturer: hit.manufacturer ?? null,
                model: hit.model || hit.part_number || model, part_number: hit.part_number ?? null,
                unit_cost: hit.average_cost ?? hit.last_purchase ?? null, unit_price: null, item_id: hit.id,
              })}
              className="inline-flex items-center gap-1 rounded-lg bg-brand-50 px-2.5 py-1 font-semibold text-brand-700 ring-1 ring-inset ring-brand-200 transition-colors hover:bg-brand-100 disabled:opacity-50">
              <Plus className="h-3.5 w-3.5" /> {add.isPending ? "Adding…" : "Add to Job"}
            </button>
          )}
        </>
      ) : (
        <span className="text-amber-700">Not on this job · not in the pricebook</span>
      )}
    </div>
  );
}

const NUM_FIELDS: Array<{ key: "seer2" | "eer2" | "hspf2" | "cooling_btuh" | "heating_btuh" | "afue"; label: string; types: AhriSystemType[] }> = [
  { key: "seer2", label: "SEER2", types: ["ac", "heat_pump", "mini_split", "other"] },
  { key: "eer2", label: "EER2", types: ["ac", "heat_pump", "mini_split", "other"] },
  { key: "hspf2", label: "HSPF2", types: ["heat_pump", "mini_split", "other"] },
  { key: "cooling_btuh", label: "Cooling Btu/h", types: ["ac", "heat_pump", "mini_split", "other"] },
  { key: "heating_btuh", label: "Heating Btu/h (47°F)", types: ["heat_pump", "mini_split", "furnace", "other"] },
  { key: "afue", label: "AFUE %", types: ["furnace", "other"] },
];

function AhriForm({ jobId, existing, initial, onDone }: { jobId: string; existing?: JobAhri; initial?: AhriDraft; onDone: () => void }) {
  const save = useSaveAhri();
  const [d, setD] = useState<AhriDraft>(() => (existing ? { ...existing } : initial ?? emptyAhriDraft()));
  const [looking, setLooking] = useState(false);
  const [msg, setMsg] = useState<{ tone: "good" | "info" | "bad"; text: string } | null>(() =>
    initial ? { tone: "good", text: "Filled in from the certificate — check it against the certificate, then Save." } : null);
  const [err, setErr] = useState<string | null>(null);
  // Changing a number or a model after Look It Up makes the row the office's own (typed).
  const set = <K extends keyof AhriDraft>(k: K, v: AhriDraft[K]) => setD((x) => ({
    ...x, [k]: v,
    ...(k !== "note" && k !== "system_type" && x.source === "energy_star" ? { source: "typed" as const, looked_up_at: null } : {}),
  }));
  const t = d.system_type;
  const show = (types: AhriSystemType[]) => !t || types.includes(t);

  const lookUp = async () => {
    setErr(null); setMsg(null); setLooking(true);
    const res = await lookupAhri(d.ahri_ref);
    setLooking(false);
    if (res.status === "found") {
      setD((x) => ({ ...res.draft, note: x.note }));
      setMsg({ tone: "good", text: "Found on ENERGY STAR's list — the models and ratings are filled in. Check them against the certificate, then Save." });
    } else if (res.status === "not_found") {
      setD((x) => ({ ...x, source: "typed", looked_up_at: null }));
      setMsg({ tone: "info", text: "Not on ENERGY STAR's free list — that list covers heat pumps and mini-splits only (it stopped listing central ACs in Feb 2026). Pick the system type and type the models and ratings off the AHRI certificate." });
    } else {
      setMsg({ tone: "bad", text: res.message });
    }
  };

  const numVal = (v: number | null) => (v == null ? "" : String(v));
  const toNum = (s: string) => (s.trim() === "" || Number.isNaN(Number(s)) ? null : Number(s));
  const canSave = cleanAhriRef(d.ahri_ref).length >= 5 && !save.isPending;

  return (
    <div className="rounded-lg border border-brand-300 bg-white p-3.5 shadow-sm">
      <div className="flex flex-wrap items-end gap-2">
        <label className="block">
          <span className="mb-1 block text-sm font-semibold text-slate-600">AHRI Number</span>
          <input autoFocus={!existing && !initial} value={d.ahri_ref} inputMode="numeric"
            onChange={(e) => { set("ahri_ref", e.target.value); setMsg(null); }}
            onKeyDown={(e) => { if (e.key === "Enter" && cleanAhriRef(d.ahri_ref).length >= 5) { e.preventDefault(); void lookUp(); } }}
            placeholder="e.g. 206905644" className={cn(inp, "w-52 font-mono tabular-nums")} />
        </label>
        <Button type="button" variant="secondary" disabled={looking || cleanAhriRef(d.ahri_ref).length < 5} onClick={() => void lookUp()}>
          {looking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Look It Up
        </Button>
        {cleanAhriRef(d.ahri_ref).length >= 5 && (
          <a href={ahriLink(d.ahri_ref, d.system_type)} target="_blank" rel="noreferrer"
            className="inline-flex h-10 items-center gap-1 px-1 text-sm font-semibold text-brand-700 hover:underline">
            Open AHRI&apos;s page <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>
      {msg && (
        <p className={cn("mt-2 rounded-lg px-3 py-2 text-sm",
          msg.tone === "good" ? "bg-emerald-50 text-emerald-800" : msg.tone === "info" ? "bg-slate-100 text-slate-700" : "bg-rose-50 text-rose-700")}>
          {msg.text}
        </p>
      )}

      <div className="mt-3">
        <span className="mb-1 block text-sm font-semibold text-slate-600">System</span>
        <div className="flex flex-wrap gap-1.5">
          {AHRI_TYPES.map((k) => (
            <button key={k} type="button" onClick={() => set("system_type", t === k ? null : k)}
              className={cn("rounded-full px-3 py-1.5 text-sm font-semibold transition-colors",
                t === k ? "bg-brand-700 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200")}>
              {AHRI_TYPE_LABEL[k]}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        {t !== "furnace" && (
          <label className="block"><span className="mb-1 block text-sm font-semibold text-slate-600">Outdoor Unit Model</span>
            <input value={d.outdoor_model ?? ""} onChange={(e) => set("outdoor_model", e.target.value || null)} className={cn(inp, "font-mono")} /></label>
        )}
        {t !== "furnace" && (
          <label className="block"><span className="mb-1 block text-sm font-semibold text-slate-600">Coil / Air Handler Model</span>
            <input value={d.indoor_model ?? ""} onChange={(e) => set("indoor_model", e.target.value || null)} className={cn(inp, "font-mono")} /></label>
        )}
        <label className="block"><span className="mb-1 block text-sm font-semibold text-slate-600">Furnace Model</span>
          <input value={d.furnace_model ?? ""} onChange={(e) => set("furnace_model", e.target.value || null)} className={cn(inp, "font-mono")} /></label>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-6">
        {NUM_FIELDS.filter((f) => show(f.types)).map((f) => (
          <label key={f.key} className="block"><span className="mb-1 block text-sm font-semibold text-slate-600">{f.key === "heating_btuh" && t === "furnace" ? "Output Btu/h" : f.label}</span>
            <input value={numVal(d[f.key])} inputMode="decimal"
              onChange={(e) => set(f.key, toNum(e.target.value))} className={cn(inp, "text-right tabular-nums")} /></label>
        ))}
      </div>

      <label className="mt-3 block"><span className="mb-1 block text-sm font-semibold text-slate-600">Note</span>
        <input value={d.note ?? ""} onChange={(e) => set("note", e.target.value || null)} placeholder="Optional — e.g. upstairs system, the rebate it's for…" className={inp} /></label>

      {err && <p className="mt-2 text-sm font-medium text-rose-600">{err}</p>}
      <div className="mt-3 flex items-center justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onDone}>Cancel</Button>
        <Button type="button" disabled={!canSave}
          onClick={() => { setErr(null); save.mutate({ id: existing?.id, jobId, draft: d }, { onSuccess: onDone, onError: (e) => setErr(e instanceof Error ? e.message : "Couldn't save.") }); }}>
          <Check className="h-4 w-4" /> {save.isPending ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}

/** The AHRI certificate(s) — job files tagged "AHRI Certificate". Click one for the viewer. */
function AhriCertificates({ job, certs, closed }: { job: Job; certs: PegasusFile[]; closed: boolean }) {
  const upload = useUploadFiles({ custno: job.custno, locno: job.locno ?? null, job_id: job.id, doc_tag: "ahri_cert", caption: "AHRI certificate" });
  const pick = useRef<HTMLInputElement>(null);
  const [note, setNote] = useState<string | null>(null);
  const [open, setOpen] = useState<PegasusFile | null>(null);
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
      <span className="font-semibold text-slate-600">Certificate:</span>
      {certs.length === 0 && <span className="text-slate-500">none attached</span>}
      {certs.map((f) => (
        <button key={f.id} type="button" onClick={() => setOpen(f)}
          className="inline-flex items-center gap-1 rounded-lg bg-white px-2.5 py-1 font-medium text-brand-700 ring-1 ring-inset ring-hairline hover:bg-brand-50">
          <FileText className="h-4 w-4" /> {f.filename}
        </button>
      ))}
      {!closed && (
        <>
          <input ref={pick} type="file" accept="application/pdf,image/*" multiple className="hidden"
            onChange={(e) => {
              const list = e.target.files ? [...e.target.files] : [];
              e.target.value = "";
              if (!list.length) return;
              setNote(null);
              upload.mutate(list, {
                onSuccess: (r) => setNote(r.skipped.length ? `Skipped: ${r.skipped.join(", ")}` : null),
                onError: (er) => setNote(er instanceof Error ? `Upload failed: ${er.message}` : "Upload failed."),
              });
            }} />
          <button type="button" onClick={() => pick.current?.click()} disabled={upload.isPending}
            className="inline-flex items-center gap-1 rounded-lg px-2 py-1 font-semibold text-brand-700 transition-colors hover:bg-brand-50 disabled:opacity-50">
            <Paperclip className="h-4 w-4" /> {upload.isPending ? "Attaching…" : "Attach the Certificate"}
          </button>
        </>
      )}
      {note && <span className="text-rose-600">{note}</span>}
      <FilePreviewModal file={open} custno={job.custno} onClose={() => setOpen(null)} />
    </div>
  );
}
